import axios from "axios";
import { FuelReport, FuelRecordsResponse } from "types/FuelReport";
import { API_URL } from "../constants";

export interface FuelReportsPagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
  hasNextPage: boolean;
}

export type FuelReportsResponse =
  | FuelReport[]
  | {
      fuelReportsCollection: FuelReport[];
      pagination: FuelReportsPagination;
    };

export async function fetchFuelReports(
  companyId: string,
  filtersValue: any,
  options: { page?: number; pageSize?: number } = {}
) {
  // Pagination is opt-in. Without `page` the backend returns the full bare
  // array (kept for any non-paginated caller of this endpoint).
  const body: any = { ...filtersValue };
  if (options && options.page != null) {
    body.page = options.page;
    body.pageSize = options.pageSize;
  }
  const res = await axios.post(
    `${API_URL}/${companyId}/fuelreport/fetch`,
    body
  );
  return res.data as FuelReportsResponse;
}

export interface FuelRecordsTotals {
  fuelAmount: number;
  additionalAmount: number;
  totalAmount: number;
  count: number;
}

export interface FuelRecordsAllResponse {
  records: any[];
  totals: FuelRecordsTotals;
  pagination: FuelReportsPagination | null;
}

/**
 * List every imported fuel transaction (FuelReportData) for a company within a
 * date range — powers the "Uploaded transactions" tab.
 */
export async function fetchAllFuelRecords(
  companyId: string,
  params: { dateFrom: string; dateTo: string; textSearch?: string },
  options: { page?: number; pageSize?: number } = {}
) {
  const body: any = { ...params };
  if (options && options.page != null) {
    body.page = options.page;
    body.pageSize = options.pageSize;
  }
  const res = await axios.post(
    `${API_URL}/${companyId}/fuel-records/all`,
    body
  );
  return res.data as FuelRecordsAllResponse;
}

export async function createFuelReport(
  companyId: string,
  data: Partial<FuelReport>
) {
  const res = await axios.post(
    `${API_URL}/${companyId}/user/fuelreport/post`,
    data
  );
  return res.data as FuelReport;
}

export async function fetchFuelReportsForCard(
  companyId: string,
  cardIds: string[],
  period: string
): Promise<FuelRecordsResponse> {
  const res = await axios.post(`${API_URL}/${companyId}/fuel-records/fetch`, {
    fuelCards: cardIds,
    period: period,
  });
  return res.data;
}

export async function fetchFuelReportById(reportId: string, companyId: string) {
  const res = await axios.get(
    `${API_URL}/${companyId}/fuelreport/${reportId}/fetch-by-id`
  );
  return res.data;
}

export async function updateFuelReport(
  companyId: string,
  id: string,
  data: any
) {
  return axios.patch(`${API_URL}/${companyId}/fuelreport/${id}/patch`, data);
}

export async function fetchLatestFuelReports(
  companyId: string,
  assetId: string,
  yearMonth: string
) {
  return axios.get(
    `${API_URL}/${companyId}/fuelreport/fetch-by-asset/${assetId}?period=${yearMonth}`
  );
}

export async function refuseFuelReport(companyId: string, reportId: string) {
  const res = await axios.patch(
    `${API_URL}/${companyId}/fuelreport/${reportId}/refuse`
  );
  return res.data;
}

export interface DeletedFuelRecordReport {
  id: string;
  period: string;
  fuelExpenses: number;
  additionalExpenses: number;
  totalPrice: number;
}

/**
 * Delete one imported transaction. The backend recomputes the totals of the
 * reports that covered it and returns them — their PDFs are not rebuilt.
 */
export async function deleteFuelRecord(
  companyId: string,
  recordId: string
): Promise<{ affectedReports: DeletedFuelRecordReport[] }> {
  const res = await axios.delete(
    `${API_URL}/${companyId}/fuel-records/${recordId}`
  );
  return res.data;
}
